import React, { useCallback, useState } from 'react';
import { Alert, Button, Card, Checkbox, Divider, Form, message, Space, Spin, Tabs, Typography } from 'antd';
import { useMutation } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { CheckCircleOutlined } from '@ant-design/icons';

import { api } from '../../utils/api-client';
import { useGetFetch } from '../../utils/reactQuery';

import { applyTemplateVariables } from './applyTemplateVariables';
import { CandidateConsent, ConsentType } from './Consents.types';

type FormValues = Record<number, boolean>;

const StyledContainer = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 15px;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;

  .ant-tabs-nav {
    margin: 0;
  }
`;

const StyledConsentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
`;

type Language = 'en' | 'pl';

const i18n = {
  en: {
    title: 'Your consents',
    invalidLinkTitle: 'Invalid link',
    invalidLinkDescription: 'This link is invalid, expired, or already used. Request a new one from your recruiter.',
    noConsentsTitle: 'No consents to review',
    noConsentsDescription: 'There are no consents awaiting your approval. If this seems wrong, contact your recruiter.',
    submittedTitle: 'Submitted',
    submittedDescription: 'Your response has been submitted.',
    expand: 'Expand',
    acceptCheckbox: 'I accept',
    submitButton: 'Accept selected consents',
    required: 'Required',
    errorCommon: 'Something went wrong',
    consentTypes: {
      DATA_PROCESSING_AND_OFFERS: 'Data Processing and Offers',
      PROJECT_PARTICIPATION: 'Project Participation',
      CLIENT_DATA_PROCESSING: 'Client Data Processing',
    },
  },
  pl: {
    title: 'Twoje zgody',
    invalidLinkTitle: 'Nieprawidłowy link',
    invalidLinkDescription: 'Ten link jest nieprawidłowy, wygasł lub został już użyty. Poproś rekrutera o nowy.',
    noConsentsTitle: 'Brak zgód do weryfikacji',
    noConsentsDescription:
      'Nie ma żadnych zgód oczekujących na Twoją akceptację. Jeśli to błąd, skontaktuj się z rekruterem.',
    submittedTitle: 'Wysłano',
    submittedDescription: 'Twoja odpowiedź została wysłana.',
    expand: 'Rozwiń',
    acceptCheckbox: 'Akceptuję',
    submitButton: 'Zaakceptuj wybrane zgody',
    required: 'Wymagane',
    errorCommon: 'Wystąpił błąd',
    consentTypes: {
      DATA_PROCESSING_AND_OFFERS: 'Przetwarzanie danych i oferty',
      PROJECT_PARTICIPATION: 'Udział w projekcie',
      CLIENT_DATA_PROCESSING: 'Przetwarzanie danych dla klienta',
    },
  },
} as const;

export const PublicConsentsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim();

  const [language, setLanguage] = useState<Language>('en');
  const t = useCallback((key: Exclude<keyof typeof i18n.en, 'consentTypes'>) => i18n[language][key], [language]);
  const tConsentType = useCallback((key: ConsentType) => i18n[language].consentTypes[key], [language]);

  const [expandedConsentIds, setExpandedConsentIds] = useState<Set<number>>(() => new Set());

  const { data, isLoading, error } = useGetFetch<CandidateConsent[]>({
    url: `/candidate/consent/with-token?token=${token}`,
    config: { enabled: !!token },
  });
  const pendingConsents = data?.filter(({ status }) => status === 'PENDING_APPROVAL');

  const {
    mutate: updateConsents,
    isLoading: isUpdating,
    isSuccess: isUpdated,
  } = useMutation({
    mutationFn: async (params: { token: string; operations: unknown[] }) => {
      await api.post(`/candidate/consent/with-token/update?token=${params.token}`, params.operations);
    },
  });

  const onSubmit = (values: FormValues) => {
    if (isUpdating) return;

    const operations = Object.entries(values).map(([consentId, isApproved]) => {
      return { consentId, operationType: isApproved ? 'APPROVE' : 'REVOKE' };
    });

    if (token) {
      updateConsents(
        { token, operations },
        {
          onError: () => {
            message.error(t('errorCommon'));
          },
        }
      );
    }
  };

  const header = (
    <StyledHeader>
      <Typography.Title level={3} style={{ margin: 0 }}>
        {t('title')}
      </Typography.Title>

      <Tabs
        activeKey={language}
        onChange={(key) => setLanguage(key as Language)}
        size="small"
        items={[
          {
            key: 'en',
            label: (
              <Space size={6}>
                <span style={{ fontSize: 16, lineHeight: 1 }}>🇬🇧</span>
                <span>English</span>
              </Space>
            ),
          },
          {
            key: 'pl',
            label: (
              <Space size={6}>
                <span style={{ fontSize: 16, lineHeight: 1 }}>🇵🇱</span>
                <span>Polski</span>
              </Space>
            ),
          },
        ]}
      />
    </StyledHeader>
  );

  if (isLoading) {
    return (
      <StyledContainer>
        <Spin size="large" />
      </StyledContainer>
    );
  }

  if (!token || !pendingConsents || error) {
    return (
      <StyledContainer>
        <Card style={{ width: '100%', maxWidth: '480px' }}>
          {header}
          <Divider />
          <Alert showIcon type="error" message={t('invalidLinkTitle')} description={t('invalidLinkDescription')} />
        </Card>
      </StyledContainer>
    );
  }

  if (pendingConsents.length === 0) {
    return (
      <StyledContainer>
        <Card style={{ width: '100%', maxWidth: '480px' }}>
          {header}
          <Divider />
          <Alert showIcon type="info" message={t('noConsentsTitle')} description={t('noConsentsDescription')} />
        </Card>
      </StyledContainer>
    );
  }

  if (isUpdated) {
    return (
      <StyledContainer>
        <Card style={{ width: '100%', maxWidth: '480px' }}>
          {header}
          <Divider />
          <Alert showIcon type="success" message={t('submittedTitle')} description={t('submittedDescription')} />
        </Card>
      </StyledContainer>
    );
  }

  const initialValues = pendingConsents.reduce<Record<number, boolean>>((values, consent) => {
    values[consent.id] = false;
    return values;
  }, {});

  return (
    <StyledContainer>
      <div style={{ width: '100%', maxWidth: '640px' }}>
        <div>{header}</div>

        <Form initialValues={initialValues} onFinish={onSubmit}>
          <Space direction="vertical" size={15} style={{ width: '100%' }}>
            {pendingConsents.map((consent) => {
              const isExpanded = expandedConsentIds.has(consent.id);
              const rawContent = language === 'en' ? consent.template.englishContent : consent.template.content;
              const content = applyTemplateVariables({
                rawContent,
                variables: {
                  CLIENT_NAME: consent.client?.name ?? null,
                  PROJECT_NAME: consent.project?.name ?? null,
                  // TODO: Add `POSITION` variable for `PROJECT_PARTICIPATION` consent type
                },
              });

              return (
                <Card key={consent.id}>
                  <StyledConsentHeader>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      {tConsentType(consent.type)}
                    </Typography.Title>

                    {!isExpanded && (
                      <Button onClick={() => setExpandedConsentIds((prev) => new Set(prev).add(consent.id))}>
                        {t('expand')}
                      </Button>
                    )}
                  </StyledConsentHeader>

                  {consent.client && <Typography.Text type="secondary">{consent.client.name}</Typography.Text>}
                  {consent.project && <Typography.Text type="secondary">{consent.project.name}</Typography.Text>}

                  {isExpanded && (
                    <>
                      <Divider />
                      <Typography.Text style={{ whiteSpace: 'pre-line' }}>{content}</Typography.Text>
                    </>
                  )}

                  <Divider />

                  <Form.Item
                    name={consent.id}
                    valuePropName="checked"
                    style={{ margin: 0 }}
                  >
                    <Checkbox>{t('acceptCheckbox')}</Checkbox>
                  </Form.Item>
                </Card>
              );
            })}

            <Button
              type="primary"
              htmlType="submit"
              icon={<CheckCircleOutlined />}
              disabled={isUpdating}
              style={{ display: 'block', margin: '0 auto' }}
            >
              {t('submitButton')}
            </Button>
          </Space>
        </Form>
      </div>
    </StyledContainer>
  );
};
